/**
 * Confirm dialog component.
 *
 * Modal alertdialog with confirm/cancel buttons. Handles:
 *   - Message rendering (HTML-escaped)
 *   - Custom or default button labels
 *   - ESC key closes + calls onCancel
 *   - Overlay click closes + calls onCancel
 *   - Focus trap with wrap (Tab and Shift+Tab)
 *   - Initial focus on Confirm, restored to previous element on close
 *   - Reduced-motion: skips fade-in via CSS media query in styles.css
 *
 * Signature matches the Batch 2 stub in src/ui/modal-stubs.js:
 *   showConfirmDialog({
 *     message, confirmLabel?, cancelLabel?,
 *     onConfirm, onCancel?
 *   })
 */

function escapeHtml(s) {
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/**
 * @param {{
 *   message: string,
 *   confirmLabel?: string,
 *   cancelLabel?: string,
 *   onConfirm: () => void,
 *   onCancel?: () => void
 * }} opts
 */
export function showConfirmDialog(opts) {
  const previouslyFocused = document.activeElement;

  const overlay = document.createElement('div');
  overlay.className = 'modal-overlay';
  overlay.innerHTML = `
    <div class="modal-dialog" role="alertdialog" aria-modal="true"
         aria-labelledby="confirm-message">
      <p id="confirm-message" class="modal-dialog__message">${escapeHtml(opts.message)}</p>
      <div class="modal-dialog__actions">
        <button type="button" class="modal-btn modal-btn--cancel" data-action="cancel">
          ${escapeHtml(opts.cancelLabel || 'Cancel')}
        </button>
        <button type="button" class="modal-btn modal-btn--confirm" data-action="confirm">
          ${escapeHtml(opts.confirmLabel || 'OK')}
        </button>
      </div>
    </div>
  `;

  const cancelBtn = overlay.querySelector('[data-action="cancel"]');
  const confirmBtn = overlay.querySelector('[data-action="confirm"]');

  function close() {
    document.removeEventListener('keydown', onKeyDown, true);
    if (overlay.parentNode) overlay.parentNode.removeChild(overlay);
    if (previouslyFocused && typeof previouslyFocused.focus === 'function') {
      try { previouslyFocused.focus(); } catch (_e) { /* no-op */ }
    }
  }

  function handleConfirm() {
    close();
    try { opts.onConfirm(); } catch (_e) { /* swallow; caller bug */ }
  }

  function handleCancel() {
    close();
    if (typeof opts.onCancel === 'function') {
      try { opts.onCancel(); } catch (_e) { /* swallow; caller bug */ }
    }
  }

  function onKeyDown(e) {
    if (e.key === 'Escape') {
      e.preventDefault();
      handleCancel();
      return;
    }
    if (e.key === 'Tab') {
      // Focus trap: two focusables -- cancel (first) and confirm (last).
      const active = document.activeElement;
      if (e.shiftKey) {
        if (active === cancelBtn) {
          e.preventDefault();
          confirmBtn.focus();
        }
      } else {
        if (active === confirmBtn) {
          e.preventDefault();
          cancelBtn.focus();
        }
      }
    }
  }

  cancelBtn.addEventListener('click', handleCancel);
  confirmBtn.addEventListener('click', handleConfirm);
  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) handleCancel();
  });
  document.addEventListener('keydown', onKeyDown, true);

  document.body.appendChild(overlay);
  confirmBtn.focus();
}
